import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Plus, Pencil, Trash2, Search, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import AdminLayout from '../../components/AdminLayout';

const API_URL = window.location.origin;

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [profileOptions, setProfileOptions] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    name: '',
    organisation_name: '',
    mobile_number: '',
    delivery_address: '',
    role: 'client',
    catering_plan: 'Small Meals',
    hte_meal_type: 'Standard Sharing/Catering Packs',
    delivery_option: 'Standard (Pre-12pm)'
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchOptions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOptions = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/settings/options`, { withCredentials: true });
      setProfileOptions(data);
    } catch (e) {
      // Error handled silently
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/users`, { withCredentials: true });
      setUsers(data);
    } catch (e) {
      // Error handled silently
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.organisation_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username || '',
        email: user.email || '',
        password: '',
        name: user.name,
        organisation_name: user.organisation_name || '',
        mobile_number: user.mobile_number || '',
        delivery_address: user.delivery_address || '',
        role: user.role,
        catering_plan: user.catering_plan || 'Small Meals',
        hte_meal_type: user.hte_meal_type || 'Standard Sharing/Catering Packs',
        delivery_option: user.delivery_option || 'Standard (Pre-12pm)'
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        password: '',
        name: '',
        organisation_name: '',
        mobile_number: '',
        delivery_address: '',
        role: 'client',
        catering_plan: profileOptions?.catering_plans?.[0] || 'Small Meals',
        hte_meal_type: profileOptions?.hte_meal_types?.[0] || 'Standard Sharing/Catering Packs',
        delivery_option: profileOptions?.delivery_options?.[0] || 'Standard (Pre-12pm)'
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingUser) {
        const updateData = { ...formData };
        delete updateData.username; // Username cannot be changed
        if (!updateData.password) delete updateData.password;
        
        await axios.put(`${API_URL}/api/admin/users/${editingUser.id}`, updateData, { withCredentials: true });
        toast.success('User updated successfully');
      } else {
        await axios.post(`${API_URL}/api/admin/users`, formData, { withCredentials: true });
        toast.success('User created successfully');
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (e) {
      // Error handled silently
      toast.error(e.response?.data?.detail || 'Failed to save user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, { withCredentials: true });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (e) {
      // Error handled silently
      toast.error('Failed to delete user');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-green-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="animate-fade-in space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Clients
            </h1>
            <p className="text-slate-500 mt-1">Manage client accounts</p>
          </div>
          <Button 
            onClick={() => handleOpenDialog()}
            className="rounded-full bg-green-600 hover:bg-green-700"
            data-testid="add-user-button"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add Client
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-xl"
            data-testid="search-users-input"
          />
        </div>

        {/* Users Table */}
        <Card className="border-slate-200/60 shadow-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Username</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden sm:table-cell">Email</TableHead>
                    <TableHead>Organisation</TableHead>
                    <TableHead className="hidden md:table-cell">Delivery</TableHead>
                    <TableHead className="hidden md:table-cell">Role</TableHead>
                    <TableHead className="hidden md:table-cell">Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((user) => (
                      <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                        <TableCell className="font-mono text-sm">{user.username || '-'}</TableCell>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell className="hidden sm:table-cell text-sm text-slate-500">{user.email || '-'}</TableCell>
                        <TableCell>{user.organisation_name || '-'}</TableCell>
                        <TableCell className="hidden md:table-cell">
                          <Badge className={`text-xs ${
                            (user.delivery_option || '').includes('Premium') ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {(user.delivery_option || 'Standard').split(' ')[0]}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <Badge className={user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {user.created_at ? format(new Date(user.created_at), 'dd MMM yyyy') : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDialog(user)}
                              className="h-8 w-8"
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {user.role !== 'admin' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(user.id)}
                                className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-user-${user.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Add/Edit User Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                {editingUser ? 'Edit Client' : 'Add New Client'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username (Login ID) *</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, '') })}
                  required
                  disabled={!!editingUser}
                  placeholder="e.g., stmarys-primary"
                  className="rounded-xl font-mono"
                  data-testid="user-username-input"
                />
                {!editingUser && (
                  <p className="text-xs text-slate-500">Lowercase letters, numbers, hyphens and underscores only</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email (for notifications)</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="rounded-xl"
                  placeholder="contact@school.com"
                  data-testid="user-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">
                  {editingUser ? 'New Password (leave blank to keep current)' : 'Password *'}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  className="rounded-xl"
                  data-testid="user-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Contact Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="rounded-xl"
                  data-testid="user-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="organisation">Organisation Name *</Label>
                <Input
                  id="organisation"
                  value={formData.organisation_name}
                  onChange={(e) => setFormData({ ...formData, organisation_name: e.target.value })}
                  required
                  className="rounded-xl"
                  data-testid="user-organisation-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mobile">Mobile Number</Label>
                <Input
                  id="mobile"
                  value={formData.mobile_number}
                  onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                  placeholder="+44 7XXX XXXXXX"
                  className="rounded-xl"
                  data-testid="user-mobile-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Delivery Address</Label>
                <textarea
                  id="address"
                  value={formData.delivery_address}
                  onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  data-testid="user-address-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="catering_plan">Catering Plan</Label>
                  <Select 
                    value={formData.catering_plan} 
                    onValueChange={(value) => setFormData({ ...formData, catering_plan: value })}
                  >
                    <SelectTrigger className="rounded-xl" data-testid="user-catering-plan-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(profileOptions?.catering_plans || ['Small Meals', 'Large Meals']).map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="hte_meal_type">HTE Meal Type</Label>
                  <Select 
                    value={formData.hte_meal_type} 
                    onValueChange={(value) => setFormData({ ...formData, hte_meal_type: value })}
                  >
                    <SelectTrigger className="rounded-xl" data-testid="user-hte-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(profileOptions?.hte_meal_types || ['Standard Sharing/Catering Packs', 'Individual Heat to Eat Meals']).map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="delivery_option">Delivery Option</Label>
                  <Select
                    value={formData.delivery_option}
                    onValueChange={(value) => setFormData({ ...formData, delivery_option: value })}
                  >
                    <SelectTrigger className="rounded-xl" data-testid="user-delivery-option-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(profileOptions?.delivery_options || ['Standard (Pre-12pm)', 'Premium (Pre-10:30am)']).map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="rounded-full bg-green-600 hover:bg-green-700"
                  data-testid="save-user-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    editingUser ? 'Update Client' : 'Create Client'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
